import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Card, 
  CardContent,
  Grid,
  TextField,
  ButtonGroup,
  Alert,
  Divider
} from '@mui/material';
import { 
  Check, 
  ArrowBack, 
  CardGiftcard as CardGiftcardIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import TossPaymentWidget from '../components/TossPaymentWidget';
import EximbayPaymentWidget from '../components/EximbayPaymentWidget';
import { useLanguageContext } from '../contexts/LanguageContext';
import { getImageUrl } from '../config';
import SEO from '../components/SEO';

// 결제 제공사: 'eximbay' (기본) | 'toss'
const PAYMENT_PROVIDER = (process.env.REACT_APP_PAYMENT_PROVIDER || 'eximbay').trim().toLowerCase();
// 헌금 기능 일시 중단 스위치 (true 로 설정하면 안내 메시지만 표시)
const IS_DONATION_DISABLED = (process.env.REACT_APP_DONATION_DISABLED || '').trim().toLowerCase() === 'true';

const DonationPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, currentLanguage } = useLanguageContext();
  const [amount, setAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successData, setSuccessData] = useState<any>(null);

  // 언어별 미리 설정된 금액
  const getPresetAmounts = () => {
    if (currentLanguage === 'en') {
      return [
        { value: '500', display: 'US$ 5.00' },
        { value: '1000', display: 'US$ 10.00' },
        { value: '2000', display: 'US$ 20.00' },
        { value: '5000', display: 'US$ 50.00' }
      ];
    } else {
      return [
        { value: '5000', display: '5,000원' },
        { value: '10000', display: '10,000원' },
        { value: '20000', display: '20,000원' },
        { value: '50000', display: '50,000원' }
      ];
    }
  };
  
  const presetAmounts = getPresetAmounts();
  
  // URL에서 worship_id 추출
  const searchParams = new URLSearchParams(location.search);
  const worshipId = searchParams.get('worship_id');

  const handleAmountSelect = (value: string) => {
    setAmount(value);
    setCustomAmount('');
  };

  const handleCustomAmountChange = (value: string) => {
    setCustomAmount(value);
    setAmount('');
  };

  const getSelectedAmount = () => {
    return customAmount || amount;
  };

  // 통화 포맷팅 함수
  const formatCurrency = (value: string | number) => {
    const numValue = typeof value === 'string' ? parseInt(value) : value;
    if (currentLanguage === 'en') {
      return `US$ ${(numValue / 100).toFixed(2)}`;
    } else {
      return `${numValue.toLocaleString()}원`;
    }
  };

  const generateOrderId = () => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    return `ORDER_${timestamp}_${random}`;
  };

  const handlePaymentSuccess = (paymentKey: string, orderId: string, amount: number) => {
    setSuccessData({ paymentKey, orderId, amount });
    setShowPaymentForm(false);
    setError(null);
  };

  const handlePaymentError = (error: string) => {
    setError(error);
    setShowPaymentForm(false);
  };

  // Eximbay 결제용 통화/금액 (영어: 입력값은 센트 단위 -> USD 소수, 한국어: KRW 정수)
  const eximbayCurrency: 'USD' | 'KRW' = currentLanguage === 'en' ? 'USD' : 'KRW';
  const getEximbayAmount = () => {
    const raw = parseInt(getSelectedAmount() || '0');
    return eximbayCurrency === 'USD' ? Math.round(raw) / 100 : raw;
  };

  const handleStartPayment = () => {
    if (IS_DONATION_DISABLED) {
      // 헌금 기능 일시 중단 안내
      alert(t('donation.notAcceptingYet'));
      return;
    }

    const selectedAmount = getSelectedAmount();
    const minAmount = currentLanguage === 'en' ? 500 : 1000; // 영어: $5.00, 한국어: 1,000원
    if (!selectedAmount || parseInt(selectedAmount) < minAmount) {
      setError(t('donation.minAmountError'));
      return;
    }
    setShowPaymentForm(true);
    setError(null);
  };

  if (successData) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Card elevation={0} sx={{ p: 4, textAlign: 'center', border: '2px solid', borderColor: 'success.main', borderRadius: 4 }}>
          <Box sx={{ mb: 3 }}>
            <Check sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom sx={{ color: 'white', fontWeight: 'bold' }}>
              {t('donation.completedTitle')}
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.85)' }}>
              {formatCurrency(successData.amount)} {t('donation.completedMessage')}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={() => navigate(worshipId ? `/worship?id=${worshipId}` : '/')}
              startIcon={<ArrowBack />}
            >
              {worshipId ? t('donation.returnToWorship') : t('donation.returnToHome')}
            </Button>
          </Box>
        </Card>
      </Container>
    );
  }

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        position: 'relative',
        py: 4,
        overflow: 'auto'
      }}
    >
      <SEO 
        title={currentLanguage === 'ko' ? '헌금' : 'Donation'}
        description={currentLanguage === 'ko' 
          ? 'He Listens to Me 온라인 예배 서비스를 위한 헌금. 당신의 후원으로 더 많은 이들에게 맞춤형 예배를 전할 수 있습니다.'
          : 'Donate to He Listens to Me online worship service. Your support helps bring personalized worship to more people.'}
        url="/donation"
        locale={currentLanguage === 'ko' ? 'ko_KR' : 'en_US'}
        noindex={true}
      />
      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 10 }}>
        {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <img 
          src={getImageUrl('hands.png')}
          alt="Praying Hands" 
          style={{ 
            width: '48px', 
            height: '48px', 
            marginBottom: '16px',
            filter: 'brightness(0) invert(1)' // 이미지를 흰색으로 만들기
          }} 
        />
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', color: 'white' }}>
          {t('donation.title')}
        </Typography>
        <Typography variant="body1" sx={{ maxWidth: 600, mx: 'auto', color: 'rgba(255, 255, 255, 0.9)' }}>
          {t('donation.subtitle')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={4} justifyContent="center">
        {/* 금액 선택 */}
        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CardGiftcardIcon 
                  sx={{ 
                    width: '24px', 
                    height: '24px',
                    color: 'primary.main',
                    opacity: 0.8
                  }} 
                />
                {t('donation.amountSelection')}
              </Typography>
              
              {!showPaymentForm && (
                <>
                  <Typography variant="body2" sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.85)' }}>
                    {t('donation.amountMessage')}
                  </Typography>

                  {/* 미리 설정된 금액 */}
                  <ButtonGroup
                    fullWidth
                    sx={{
                      mb: 3,
                      '& .MuiButton-outlined': {
                        color: 'white',
                        borderColor: 'rgba(255, 255, 255, 0.6)'
                      }
                    }}
                  >
                    {presetAmounts.map((preset) => (
                      <Button
                        key={preset.value}
                        variant={amount === preset.value ? 'contained' : 'outlined'}
                        onClick={() => handleAmountSelect(preset.value)}
                        sx={{ py: 1.5 }}
                      >
                        {preset.display}
                      </Button>
                    ))}
                  </ButtonGroup>

                  {/* 직접 입력 */}
                  <TextField
                    fullWidth
                    label={t('donation.customAmount')}
                    type="number"
                    value={customAmount}
                    onChange={(e) => handleCustomAmountChange(e.target.value)}
                    placeholder={t('donation.customAmountPlaceholder')}
                    InputProps={{
                      endAdornment: <Typography variant="body2">{currentLanguage === 'en' ? 'USD' : '원'}</Typography>
                    }}
                    sx={{
                      mb: 3,
                      '& .MuiInputBase-input': { color: 'white' },
                      '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.8)' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.6)' },
                      '& .MuiTypography-root': { color: 'rgba(255, 255, 255, 0.85)' }
                    }}
                  />

                  {/* 선택된 금액 표시 */}
                  {getSelectedAmount() && (
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: 'primary.light', 
                      borderRadius: 2, 
                      mb: 3,
                      textAlign: 'center'
                    }}>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                        {t('donation.selectedAmount')}: {formatCurrency(getSelectedAmount())}
                      </Typography>
                    </Box>
                  )}

                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleStartPayment}
                    disabled={!getSelectedAmount()}
                    sx={{ py: 1.5, fontSize: '1.1rem', fontWeight: 'bold' }}
                  >
                    {t('donation.payButton')}
                  </Button>
                </>
              )}

              {/* 결제 위젯 (기본: 엑심베이, REACT_APP_PAYMENT_PROVIDER=toss 시 토스페이먼츠) */}
              {showPaymentForm && (
                <Box sx={{ mt: 3 }}>
                  {PAYMENT_PROVIDER === 'toss' ? (
                    <TossPaymentWidget
                      amount={parseInt(getSelectedAmount())}
                      orderId={generateOrderId()}
                      orderName={`${t('donation.title')} ${formatCurrency(getSelectedAmount())}`}
                      onSuccess={handlePaymentSuccess}
                      onError={handlePaymentError}
                      worshipId={worshipId || undefined}
                    />
                  ) : (
                    <EximbayPaymentWidget
                      amount={getEximbayAmount()}
                      currency={eximbayCurrency}
                      displayAmount={formatCurrency(getSelectedAmount())}
                      onError={handlePaymentError}
                      worshipId={worshipId || undefined}
                    />
                  )}
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => setShowPaymentForm(false)}
                    sx={{ mt: 2 }}
                  >
                    {t('donation.cancel')}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        
      </Grid>

      {/* 뒤로가기 버튼 */}
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Button
          onClick={() => navigate(worshipId ? `/worship?id=${worshipId}` : '/')}
          startIcon={<ArrowBack />}
          sx={{ color: 'white', '&:hover': { color: 'rgba(255, 255, 255, 0.8)' } }}
        >
          {worshipId ? t('donation.returnToWorshipText') : t('donation.returnToHomeText')}
        </Button>
      </Box>

      <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.2)' }} />

      {/* 보안 안내 */}
      <Box sx={{ textAlign: 'center', p: 3, bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'white' }}>
          {t('donation.securePaymentTitle')}
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: 'rgba(255, 255, 255, 0.9)' }}>
          {PAYMENT_PROVIDER === 'toss'
            ? t('donation.securePaymentDescription')
            : t('donation.securePaymentDescriptionEximbay')}
        </Typography>
      </Box>
      </Container>
    </Box>
  );
};

export default DonationPage;